// PayGate operational metrics (admin-portal) — read-only payout throughput, per-connector
// rollups, and reconciliation state from paygate-service. All via the gateway at
// /api/v1/paygate/admin/metrics/* (platform-role-guarded). An optional tenantId narrows
// to one tenant; omitted = platform-wide. Connector HEALTH is read separately from the
// routing providers endpoint (see routingService.listProviders).
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/paygate/admin/metrics`;
const ADMIN = `${API_V1}/paygate/admin`;

export interface Summary {
  total_items: number;
  paid: number;
  failed: number;
  in_flight: number;
  settled_amount: number;
  open_recon_cases: number;
}
export interface DayPoint {
  day: string;
  paid: number;
  failed: number;
  in_flight: number;
  amount: number;
}
export interface ConnectorRow {
  connector: string;
  total: number;
  paid: number;
  failed: number;
  amount: number;
}
export interface StatusCount {
  status: string;
  count: number;
}

const q = (tenantId?: string) => (tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '');
const qd = (days: number, tenantId?: string) =>
  `?days=${days}${tenantId ? `&tenantId=${encodeURIComponent(tenantId)}` : ''}`;

export const getSummary = (tenantId?: string) =>
  axios.get<Summary>(`${BASE}/summary${q(tenantId)}`).then((r) => r.data);

export const getThroughput = (days: number, tenantId?: string) =>
  axios.get<{ points: DayPoint[] }>(`${BASE}/throughput${qd(days, tenantId)}`).then((r) => r.data.points ?? []);

export const getByConnector = (tenantId?: string) =>
  axios.get<{ connectors: ConnectorRow[] }>(`${BASE}/by-connector${q(tenantId)}`).then((r) => r.data.connectors ?? []);

export const getReconciliation = (tenantId?: string) =>
  axios.get<{ cases: StatusCount[] }>(`${BASE}/reconciliation${q(tenantId)}`).then((r) => r.data.cases ?? []);

// A payout batch held for verification (breached a per-tenant sanity cap, or the dual-control
// gate). hold_reason explains why. Read-only ops view; releasing happens in the tenant workspace.
export interface HeldBatch {
  id: string;
  tenant_id: string;
  source_system: string;
  source_reference: string;
  status: string;
  hold_reason?: string;
  currency: string;
  total_amount: number;
  item_count: number;
  created_at: string;
}

// Held payout batches across tenants (oldest first). Omit tenantId = platform-wide.
export const listHeldBatches = (tenantId?: string) =>
  axios.get<{ held: HeldBatch[] }>(`${ADMIN}/payouts/held${q(tenantId)}`).then((r) => r.data.held ?? []);
